import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EventIcon from "@material-ui/icons/Event";
import GradeIcon from "@material-ui/icons/Grade";
import RefreshIcon from "@material-ui/icons/Refresh";
import SaveIcon from "@material-ui/icons/Save";
import TimerIcon from "@material-ui/icons/Timer";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ListAltIcon from "@material-ui/icons/ListAlt";
import managerTestApi from "api/managerTest";
import ExamQuestionCard from "components/ExamQuestionCard";
import { ResponseStatus } from "models/common";
import { ListQuestion, Question } from "models/managerTest";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { deduplicateQuestions, isLimitedDisplay, pickDisplayedQuestions } from "utils/examPreview";
import { timeConvert } from "utils/timeConvert";

export interface DetailExamProps {
  valueForm: Question | undefined;
  listQuestion: ListQuestion | any;
  setShowDetailExam: (e: any) => void;
  valueInputShuffle?: any;
}

export default function DetailExam({
  listQuestion,
  valueForm,
  setShowDetailExam,
  valueInputShuffle,
}: DetailExamProps) {
  const useStyles = makeStyles((theme) => ({
    wrapper: {
      width: "100%",
      minHeight: "calc(100vh - 200px)",
      display: "flex",
      flexDirection: "column",
    },
    metaCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: "24px 28px",
      marginBottom: 20,
      border: "1px solid #E2E8F0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
    },
    metaTitle: {
      fontSize: "1.25rem",
      fontWeight: 800,
      color: "#0F172A",
      marginBottom: 16,
      lineHeight: 1.4,
    },
    statBadge: {
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      borderRadius: 12,
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    statIcon: {
      color: "#2563EB",
      fontSize: 22,
    },
    statValue: {
      fontSize: "0.95rem",
      fontWeight: 700,
      color: "#0F172A",
    },
    statLabel: {
      fontSize: "0.75rem",
      fontWeight: 600,
      color: "#64748B",
    },
    bannerPreview: {
      backgroundColor: "#EFF6FF",
      borderRadius: 16,
      border: "1px solid #BFDBFE",
      padding: "14px 20px",
      marginBottom: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
    },
    stickyFooter: {
      position: "sticky",
      bottom: 16,
      zIndex: 100,
      backgroundColor: "rgba(255, 255, 255, 0.94)",
      backdropFilter: "blur(12px)",
      borderRadius: 16,
      padding: "14px 24px",
      border: "1px solid #E2E8F0",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.1)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "auto",
      [theme.breakpoints.down("sm")]: {
        padding: "12px 14px",
        bottom: 8,
        borderRadius: 14,
        gap: 8,
        width: "100%",
      },
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
      color: "#FFFFFF",
      borderRadius: 12,
      padding: "10px 24px",
      fontWeight: 700,
      fontSize: "0.9rem",
      textTransform: "none",
      boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
      },
      [theme.breakpoints.down("sm")]: {
        flex: 1,
        padding: "12px 14px",
        fontSize: "0.85rem",
      },
    },
    btnSecondary: {
      backgroundColor: "#F1F5F9",
      color: "#475569",
      borderRadius: 12,
      padding: "10px 20px",
      fontWeight: 600,
      fontSize: "0.9rem",
      textTransform: "none",
      border: "1px solid #CBD5E1",
      "&:hover": {
        backgroundColor: "#E2E8F0",
        color: "#0F172A",
      },
      [theme.breakpoints.down("sm")]: {
        flex: 1,
        padding: "12px 14px",
        fontSize: "0.85rem",
      },
    },
  }));

  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();

  const [disabledBtnSend, setDisableBtnSend] = React.useState(false);
  const [previewAsStudent, setPreviewAsStudent] = React.useState(true);
  const [shuffleKey, setShuffleKey] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent.tagName !== "MAIN" && parent.tagName !== "BODY") {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, []);

  const uniqueQuestions = React.useMemo<ListQuestion[]>(
    () => deduplicateQuestions<ListQuestion>(listQuestion ?? []),
    [listQuestion]
  );

  const displayedQuestions = React.useMemo(() => {
    if (!previewAsStudent) {
      return uniqueQuestions;
    }
    return pickDisplayedQuestions(uniqueQuestions, valueInputShuffle);
  }, [uniqueQuestions, valueInputShuffle, previewAsStudent, shuffleKey]);

  const showPreviewBanner = isLimitedDisplay(valueInputShuffle, uniqueQuestions.length);

  const handleSendForm = async () => {
    setDisableBtnSend(true);
    setIsLoading(true);
    const value = {
      title: valueForm?.title,
      questionList: uniqueQuestions.map((question: ListQuestion) => ({
        answers: question.answers,
        point: question.point,
        questionContent: question.questionContent,
        imageFileIDList: question.imageFileIDList.map((x: any) => x.id),
      })),
      examTime: valueForm?.examTime,
      prizeId: valueForm?.prizeId,
      startDate: valueForm?.startDate,
      totalPoint: valueForm?.totalPoint,
      type: valueForm?.type,
      departmentIdList: valueForm?.departmentId,
      publishAnswerDate: valueForm?.publishAnswerDate,
      hiddenAnswerDate: valueForm?.hiddenAnswerDate,
      classIdList: valueForm?.classId,
      userIdList: valueForm?.userIdList,
      numberOfDisplayedQuestion:
        valueInputShuffle != null && valueInputShuffle > 0
          ? Math.min(valueInputShuffle, uniqueQuestions.length)
          : null,
    };
    try {
      const res: ResponseStatus = await managerTestApi.createExam(value);
      if (res.responseCode === "00") {
        toast.success(`${t("managerTest.taodethithanhcong")}`);
        navigate("/admin/manager-test/list");
      } else {
        toast.error(`${t("managerTest.taodethithatbai")}`);
        setDisableBtnSend(false);
        setIsLoading(false);
      }
    } catch (error) {
      toast.error(`${t("managerTest.taodethithatbai")}`);
      setDisableBtnSend(false);
      setIsLoading(false);
    }
  };

  return (
    <div ref={containerRef} className={classes.wrapper}>
      {/* Exam Summary Metadata Card */}
      <Paper className={classes.metaCard} elevation={0}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 12 }}>
          <Box>
            <Typography className={classes.metaTitle}>
              {valueForm?.title || "Xem Trước Đề Thi Mới"}
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 10 }}>
              <Chip
                label={valueForm?.category === "essay" ? "Tự luận" : "Trắc nghiệm"}
                size="small"
                style={{ backgroundColor: "#38BDF8", color: "#0F172A", fontWeight: 700 }}
              />
              <Chip
                label="Bản nháp bài thi mới"
                size="small"
                style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontWeight: 600 }}
              />
            </Box>
          </Box>
        </Box>

        <Grid container spacing={2} style={{ marginTop: 12 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box className={classes.statBadge}>
              <EventIcon className={classes.statIcon} />
              <Box>
                <Typography className={classes.statLabel}>Bắt đầu làm bài</Typography>
                <Typography className={classes.statValue}>
                  {valueForm?.startDate ? valueForm.startDate.replace("T", " ") : "Chưa đặt"}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box className={classes.statBadge}>
              <TimerIcon className={classes.statIcon} style={{ color: "#0284C7" }} />
              <Box>
                <Typography className={classes.statLabel}>Thời gian thi</Typography>
                <Typography className={classes.statValue}>
                  {timeConvert(Number(valueForm?.examTime || 0))}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box className={classes.statBadge}>
              <ListAltIcon className={classes.statIcon} style={{ color: "#8B5CF6" }} />
              <Box>
                <Typography className={classes.statLabel}>Số lượng câu hỏi</Typography>
                <Typography className={classes.statValue}>
                  {uniqueQuestions.length} câu
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box className={classes.statBadge}>
              <GradeIcon className={classes.statIcon} style={{ color: "#F59E0B" }} />
              <Box>
                <Typography className={classes.statLabel}>Tổng điểm thi</Typography>
                <Typography className={classes.statValue}>
                  {valueForm?.totalPoint} điểm
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Banner Preview Filter */}
      {showPreviewBanner && (
        <Box className={classes.bannerPreview}>
          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            <VisibilityIcon style={{ color: "#2563EB", fontSize: 20 }} />
            <Typography variant="body2" style={{ fontWeight: 600, color: "#1E40AF" }}>
              {previewAsStudent
                ? `Đang xem thử góc nhìn học viên: Random ${displayedQuestions.length} / ${uniqueQuestions.length} câu`
                : `Đang xem toàn bộ ${uniqueQuestions.length} câu trong đề`}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPreviewAsStudent((v) => !v)}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
            >
              {previewAsStudent ? "Xem tất cả câu hỏi" : "Xem thử học viên"}
            </Button>
            {previewAsStudent && (
              <Button
                size="small"
                variant="contained"
                startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
                onClick={() => setShuffleKey((k) => k + 1)}
                style={{
                  backgroundColor: "#2563EB",
                  color: "#FFFFFF",
                  borderRadius: 8,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Xáo trộn ngẫu nhiên
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Questions Preview List */}
      <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
        {displayedQuestions?.map((item: ListQuestion, index: number) => (
          <ExamQuestionCard
            key={index}
            question={item}
            index={index}
            mode="view"
            isEssay={valueForm?.category === "essay"}
          />
        ))}
      </Box>

      {/* Sticky Bottom Action Footer */}
      <Paper className={classes.stickyFooter} elevation={0}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => setShowDetailExam(false)}
          className={classes.btnSecondary}
          disabled={isLoading}
        >
          Quay Lại
        </Button>

        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          className={classes.btnPrimary}
          onClick={handleSendForm}
          disabled={disabledBtnSend || isLoading}
        >
          Tạo Đề Thi
        </Button>
      </Paper>

      <Backdrop
        style={{ zIndex: 9999, color: "#fff", flexDirection: "column", gap: 16 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" style={{ fontWeight: 600, color: "#fff" }}>
          Đang tạo đề thi, vui lòng chờ...
        </Typography>
      </Backdrop>
    </div>
  );
}
